/**
 * rm-search.js
 * Predictive search for Rustic & Main.
 * Drives both:
 *   - Desktop: full-width header dropdown (#rm-search-dropdown)
 *   - Mobile:  drawer search panel (#drawer-search-panel)
 *
 * Custom elements registered:
 *   <rm-predictive-search> — wraps each search form + results container
 */

(function () {
  'use strict';

  /* ── Debounce util ── */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ── Recent searches (localStorage, per-browser) ──
     Wrapped in try/catch: localStorage can throw in private-browsing /
     locked-down contexts, and that should never break search itself. */
  var RECENT_KEY = 'rm_recent_searches';
  var RECENT_MAX = 3;

  function getRecentSearches() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecentSearch(term) {
    term = (term || '').trim();
    if (!term) return;
    try {
      var list = getRecentSearches();
      var lower = term.toLowerCase();
      list = list.filter(function (t) { return t.toLowerCase() !== lower; });
      list.unshift(term);
      if (list.length > RECENT_MAX) list.length = RECENT_MAX;
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable — recent searches just won't persist */
    }
  }

  /* ═══════════════════════════════════════════════
     RMPredictiveSearch custom element
     One instance for desktop, one for mobile drawer
  ═══════════════════════════════════════════════ */
  class RMPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.resultsContainer = null;
      this.clearBtn = null;
      this.cachedResults = {};
      this.searchTerm = '';
      this.abortController = new AbortController();
      this.isOpen = false;
    }

    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector('[data-rm-search-results]');
      this.clearBtn = this.querySelector('[data-rm-search-clear]') || this.querySelector('.rm-search-dropdown__clear');
      if (!this.input || !this.resultsContainer) return;

      /* Debounced input handler (fetch results) */
      this.input.addEventListener('input', debounce(this.onInput.bind(this), 300));
      /* Un-debounced: keep the clear button's visibility in sync immediately */
      this.input.addEventListener('input', this.updateClearVisibility.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));

      if (this.clearBtn) this.clearBtn.addEventListener('click', this.onClear.bind(this));
      this.updateClearVisibility();

      /* Delegate clicks on recent-search chips (re-injected on every render) */
      this.resultsContainer.addEventListener('click', this.onRecentChipClick.bind(this));

      /* Prevent form submission if predictive results are selected */
      var form = this.input.closest('form');
      if (form) form.addEventListener('submit', this.onSubmit.bind(this));
    }

    /* Show/hide the reset button based on whether there's a query to clear.
       Kept separate from close(): clearing the query resets the field and
       results but leaves the search dropdown/panel open, unlike the X
       (close) button, which dismisses the whole thing. */
    updateClearVisibility() {
      if (!this.clearBtn) return;
      this.clearBtn.hidden = this.input.value.length === 0;
    }

    onClear(e) {
      e.preventDefault();
      this.input.value = '';
      this.searchTerm = '';
      this.resultsContainer.innerHTML = '';
      this.removeAttribute('results');
      this.updateClearVisibility();
      this.input.focus();
    }

    onInput() {
      var term = this.input.value.trim();
      if (term === this.searchTerm) return;
      this.searchTerm = term;
      if (!term.length) { this.close(); return; }
      this.fetchResults(term);
    }

    onFocus() {
      if (this.searchTerm && this.getAttribute('results') === 'true') this.open();
      else if (this.searchTerm) this.fetchResults(this.searchTerm);
      else this.showRecentSearches();
    }

    /* Empty box, just focused: offer up to RECENT_MAX past searches as
       one-click chips, so a clean close doesn't lose easy access to them. */
    showRecentSearches() {
      var recent = getRecentSearches();
      if (!recent.length) return;
      var chips = recent.map(function (term) {
        return '<button type="button" class="rm-ps__recent-chip" data-rm-recent-term="' +
          term.replace(/"/g, '&quot;') + '">' + term.replace(/</g, '&lt;') + '</button>';
      }).join('');
      this.resultsContainer.innerHTML =
        '<div class="rm-ps__recent"><span class="rm-ps__recent-label">Recent searches</span>' + chips + '</div>';
      this.open();
    }

    onRecentChipClick(e) {
      var chip = e.target.closest('[data-rm-recent-term]');
      if (!chip) return;
      var term = chip.getAttribute('data-rm-recent-term');
      this.input.value = term;
      this.searchTerm = term;
      this.updateClearVisibility();
      this.fetchResults(term);
      this.input.focus();
    }

    onFocusOut() {
      setTimeout(function () {
        if (!this.contains(document.activeElement)) this.close();
      }.bind(this), 150);
    }

    onSubmit(e) {
      saveRecentSearch(this.input.value);
      var selected = this.querySelector('[aria-selected="true"] a');
      if (selected) { e.preventDefault(); selected.click(); }
    }

    onKeydown(e) {
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); this.moveSelection(1); break;
        case 'ArrowUp':   e.preventDefault(); this.moveSelection(-1); break;
        case 'Enter':     this.selectHighlighted(); break;
        case 'Escape':    this.close(true); break;
      }
    }

    moveSelection(dir) {
      var items = Array.from(this.querySelectorAll('.rm-ps__result[role="option"]'));
      if (!items.length) return;
      var current = this.querySelector('[aria-selected="true"]');
      var idx = items.indexOf(current);
      if (current) current.setAttribute('aria-selected', 'false');
      var next = items[(idx + dir + items.length) % items.length];
      next.setAttribute('aria-selected', 'true');
      next.querySelector('a')?.focus();
    }

    selectHighlighted() {
      var selected = this.querySelector('[aria-selected="true"] a');
      if (selected) selected.click();
    }

    fetchResults(term) {
      var key = term.toLowerCase();
      if (this.cachedResults[key]) {
        this.renderResults(this.cachedResults[key]);
        return;
      }

      this.setAttribute('loading', '');
      this.abortController.abort();
      this.abortController = new AbortController();

      /* Which resource types to fetch — defaults to the header's full mix.
         Set data-resource-types="article" (etc.) on the <rm-predictive-search>
         element to scope an instance to just one kind, e.g. the blog index's
         own article-only search bar. */
      var resourceTypes = this.getAttribute('data-resource-types') || 'product,article,page';

      var url = window.Shopify
        ? '/search/suggest?q=' + encodeURIComponent(term) + '&resources[type]=' + resourceTypes + '&resources[limit]=5&section_id=predictive-search'
        : null;

      if (!url) return;

      fetch(url, { signal: this.abortController.signal })
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then(function (html) {
          /* Shopify wraps section HTML in a div#shopify-section-predictive-search */
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('#shopify-section-predictive-search');
          var markup = inner ? inner.innerHTML : '';
          this.cachedResults[key] = markup;
          this.renderResults(markup);
        }.bind(this))
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          this.removeAttribute('loading');
        }.bind(this));
    }

    renderResults(markup) {
      this.removeAttribute('loading');
      this.resultsContainer.innerHTML = markup;
      this.setAttribute('results', 'true');
      this.open();
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      /* Notify parent dropdown/panel to show */
      this.dispatchEvent(new CustomEvent('rm-search:open', { bubbles: true }));
    }

    close(clearTerm) {
      if (!this.isOpen && !clearTerm) return;
      this.isOpen = false;
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      if (clearTerm) {
        /* Record what was actually searched before wiping it, so closing
           search doesn't also throw away the "recent searches" shortcut. */
        saveRecentSearch(this.searchTerm);
        this.input.value = '';
        this.searchTerm = '';
        this.removeAttribute('results');
        this.resultsContainer.innerHTML = '';
        this.updateClearVisibility();
      }
      /* Reset aria-selected */
      this.querySelectorAll('[aria-selected="true"]').forEach(function (el) {
        el.setAttribute('aria-selected', 'false');
      });
      this.dispatchEvent(new CustomEvent('rm-search:close', { bubbles: true }));
    }
  }

  customElements.define('rm-predictive-search', RMPredictiveSearch);


  /* ═══════════════════════════════════════════════
     Desktop search dropdown
  ═══════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', function () {

    var toggleBtn  = document.getElementById('search-toggle');
    var dropdown   = document.getElementById('rm-search-dropdown');
    var closeBtn   = document.getElementById('rm-search-close');
    var psEl       = document.getElementById('rm-search-desktop');

    if (!toggleBtn || !dropdown) return;

    function openDropdown() {
      dropdown.classList.add('is-open');
      toggleBtn.setAttribute('aria-expanded', 'true');
      var input = dropdown.querySelector('input[type="search"]');
      if (input) setTimeout(function () { input.focus(); }, 50);
      document.body.classList.add('rm-search-open');
    }

    function closeDropdown() {
      dropdown.classList.remove('is-open');
      toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('rm-search-open');
      if (psEl) psEl.close(true);
    }

    toggleBtn.addEventListener('click', function () {
      dropdown.classList.contains('is-open') ? closeDropdown() : openDropdown();
    });

    if (closeBtn) closeBtn.addEventListener('click', closeDropdown);

    /* Close on outside click.
       Use composedPath() (captured at dispatch time) instead of Node.contains().
       Clicking a recent-search chip re-renders resultsContainer.innerHTML
       synchronously (when the term is already cached), which detaches the
       clicked chip from the document before this bubbles up here — contains()
       then wrongly returns false for the now-detached node and closes the
       dropdown out from under the click. composedPath() reflects the tree as
       it was when the event fired, so it's unaffected by that mutation. */
    document.addEventListener('click', function (e) {
      var path = typeof e.composedPath === 'function' ? e.composedPath() : null;
      var insideDropdown = path ? path.indexOf(dropdown) > -1 : dropdown.contains(e.target);
      var insideToggle = path ? path.indexOf(toggleBtn) > -1 : (e.target === toggleBtn || toggleBtn.contains(e.target));
      if (!insideDropdown && !insideToggle) {
        closeDropdown();
      }
    });

    /* Close on Escape */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && dropdown.classList.contains('is-open')) closeDropdown();
    });

    /* Keep dropdown open while predictive results are showing */
    if (psEl) {
      psEl.addEventListener('rm-search:open', function () {
        dropdown.classList.add('is-open');
      });
    }
  });

  /* ═══════════════════════════════════════════════
     Mobile drawer search (upgrade existing form)
  ═══════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', function () {
    var mobilePS = document.getElementById('rm-search-mobile');
    if (!mobilePS) return;

    var resultsPanel = document.getElementById('rm-search-mobile-results');
    if (!resultsPanel) return;

    mobilePS.addEventListener('rm-search:open', function () {
      resultsPanel.classList.add('is-visible');
    });

    mobilePS.addEventListener('rm-search:close', function () {
      resultsPanel.classList.remove('is-visible');
    });
  });

  /* ═══════════════════════════════════════════════
     Wildcard search submit

     Storefront search (/search) uses a DIFFERENT engine than predictive search
     (/search/suggest) and does not do partial-word matching. options[prefix]=last
     is documented but unreliable, so we append an explicit trailing '*' to the last
     term. This is what makes "thunder" return "The Thunderbird" on the results page,
     matching what the dropdown already shows.

     Delegated on document so it covers every search form (header dropdown, mobile
     drawer, and the form on the search results page itself).
  ═══════════════════════════════════════════════ */
  function rmWildcardTerm(raw) {
    var term = (raw || '').trim();
    if (!term) return term;

    /* Respect explicit search syntax — quoted phrases, field:value, existing
       wildcards, and boolean operators are all left untouched. */
    if (/["*:]/.test(term)) return term;
    if (/\b(AND|OR|NOT)\b/.test(term)) return term;

    var parts = term.split(/\s+/);
    var last = parts[parts.length - 1];

    /* Shopify caps prefix expansion at 50 matches, so very short prefixes are
       counterproductive — they blow the cap and drop relevant products. */
    if (last.length < 3) return term;

    parts[parts.length - 1] = last + '*';
    return parts.join(' ');
  }

  document.addEventListener('submit', function (e) {
    /* Predictive search calls preventDefault when a suggestion is highlighted —
       in that case we're navigating to a product, not searching. */
    if (e.defaultPrevented) return;

    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/search') === -1) return;

    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    /* Wildcard append disabled (see block comment above rmWildcardTerm) —
       trailing '*' turns this into a Shopify "syntax query", which skips
       typo tolerance/semantic matching AND isn't logged in Shopify's search
       analytics reports. Confirmed with Shopify support; this silently
       zeroed out search reporting from the day it was added. Submitting the
       term exactly as typed instead, so tracking and native search quality
       are both restored. rmWildcardTerm() is left defined below, unused, in
       case broader matching is needed again later — though look at
       Shopify's "Product boost" feature first, since that doesn't touch the
       tracked query string at all. */
  });

})();
